import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

const testimonials = [
  {
    id: 1,
    name: "Sarah Mitchell",
    location: "Melbourne, VIC",
    rating: 5,
    text: "The quality of Colin's artwork is extraordinary. The colours are vibrant and the story behind each piece makes it truly special. My limited edition print is the centrepiece of our living room.",
    type: "Collector",
  },
  {
    id: 2,
    name: "James Chen",
    location: "Sydney, NSW",
    rating: 5,
    text: "We purchased several pieces for our hotel lobby and the response from guests has been incredible. The certificate of authenticity adds tremendous value. Will definitely be ordering more.",
    type: "Trade Buyer",
  },
  {
    id: 3,
    name: "Emma Thompson",
    location: "Brisbane, QLD",
    rating: 5,
    text: "From browsing to delivery, the experience was seamless. The artwork arrived beautifully packaged and the cultural story card included with it made the purchase even more meaningful.",
    type: "Collector",
  },
  {
    id: 4,
    name: "Michael Rivera",
    location: "Perth, WA",
    rating: 5,
    text: "As a gallery owner, I appreciate the wholesale program and the quality of prints. The 'View on Wall' AR feature helps my clients visualise pieces in their spaces before buying.",
    type: "Gallery Owner",
  },
];

const Testimonials = () => {
  const [current, setCurrent] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  useEffect(() => {
    if (!autoPlay) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [autoPlay]);

  const next = () => {
    setAutoPlay(false);
    setCurrent((prev) => (prev + 1) % testimonials.length);
  };

  const prev = () => {
    setAutoPlay(false);
    setCurrent((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const t = testimonials[current];

  return (
    <section className="py-24 px-6 md:px-12 bg-card/50" data-testid="testimonials-section">
      <div className="max-w-4xl mx-auto text-center">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-sm tracking-widest uppercase text-primary mb-2"
        >
          What Collectors Say
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-3xl md:text-4xl font-brand mb-12"
        >
          Trusted by Art Lovers Worldwide
        </motion.h2>

        <div className="relative min-h-[250px] flex items-center">
          {/* Nav buttons */}
          <button
            onClick={prev}
            className="absolute left-0 -ml-2 md:-ml-12 w-10 h-10 rounded-full bg-muted/50 hover:bg-muted flex items-center justify-center transition-colors z-10"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={next}
            className="absolute right-0 -mr-2 md:-mr-12 w-10 h-10 rounded-full bg-muted/50 hover:bg-muted flex items-center justify-center transition-colors z-10"
            aria-label="Next testimonial"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          <AnimatePresence mode="wait">
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="w-full px-8 md:px-16"
            >
              <Quote className="h-10 w-10 text-primary/20 mx-auto mb-6" />

              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 italic">
                "{t.text}"
              </p>

              <div className="flex items-center justify-center gap-1 mb-3">
                {Array.from({ length: t.rating }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                ))}
              </div>

              <p className="font-medium">{t.name}</p>
              <p className="text-sm text-muted-foreground">
                {t.type} &middot; {t.location}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mt-8">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                setAutoPlay(false);
                setCurrent(i);
              }}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                i === current ? "bg-primary w-6" : "bg-muted-foreground/30"
              }`}
              aria-label={`Go to testimonial ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
